import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, Edit, EmojiEvents } from '@mui/icons-material';
import { FC, useState } from 'react';

import { useAuth } from '@carcareers/hooks';
import { useJobseekerDeleteCertificateMutation, useJobseekerCertificatesQuery } from '@carcareers/store';
import { JobseekerCertificateModal } from '@carcareers/components';
import { JobseekerCertificate } from '@libs/models';

export const CertificatesCard: FC = () => {
  const { session } = useAuth();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editCertificate, setEditCertificate] = useState<JobseekerCertificate | null>(null);

  const [deleteCertificate, { isLoading: isDeleting }] = useJobseekerDeleteCertificateMutation();
  const { data: certificates } = useJobseekerCertificatesQuery(session?.profile?.id ?? 0, {
    skip: !session?.profile?.id,
  });

  const handleOpenAdd = () => setIsAddOpen(true);
  const handleCloseAdd = () => setIsAddOpen(false);

  const handleEditCertificate = (certificate: JobseekerCertificate) => {
    setEditCertificate(certificate);
  };

  const handleCloseEdit = () => {
    setEditCertificate(null);
  };

  const handleDeleteCertificate = (certificateId: number) => {
    const jobseekerId = session?.profile?.id ?? 0;
    deleteCertificate({ jobseekerId, certificateId });
  };

  const formatDate = (date?: Date | null): string => {
    if (!date) return '';
    const newDate = new Date(date);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' };
    return new Intl.DateTimeFormat('en-US', options).format(newDate);
  };

  return (
    <>
      <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: '16px !important' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
              <EmojiEvents sx={{ mr: 1 }} />
              Certificates
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              size="small"
              sx={{
                borderRadius: 2,
                height: 32,
                mt: { xs: 2, md: 0 },
                display: { xs: 'none', md: 'flex' },
              }}
              onClick={handleOpenAdd}
            >
              Add Certificate
            </Button>
          </Box>

          <List
            sx={{
              '& .MuiListItemSecondaryAction-root': {
                position: 'unset',
                top: '32px',
                transform: 'unset',
              },
            }}
          >
            {certificates?.map((certificate, index) => (
              <Box key={certificate.id}>
                <ListItem
                  sx={{ px: 0 }}
                  alignItems="flex-start"
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        color="secondary"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleEditCertificate(certificate)}
                      >
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="secondary"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleDeleteCertificate(certificate.id)}
                      >
                        {isDeleting ? <CircularProgress size={18} color="secondary" /> : <Delete sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={certificate.name}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="primary">
                          {certificate.issuingAuthority}
                        </Typography>
                        {certificate.issueDate && (
                          <Typography variant="caption" color="text.secondary">
                            Issued: {formatDate(certificate.issueDate)}
                          </Typography>
                        )}
                        {certificate.description && (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 1,
                              display: '-webkit-box',
                              WebkitLineClamp: 4,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              position: 'relative',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '1.5em',
                                background: 'linear-gradient(transparent, white)',
                                pointerEvents: 'none',
                              },
                            }}
                          >
                            {certificate.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < (certificates?.length ?? 0) - 1 && <Divider />}
              </Box>
            ))}
          </List>

          <Button
            variant="outlined"
            startIcon={<Add />}
            size="small"
            sx={{
              borderRadius: 2,
              height: 32,
              width: '100%',
              mt: 2,
              display: { xs: 'flex', md: 'none' },
            }}
            onClick={handleOpenAdd}
          >
            Add Certificate
          </Button>
        </CardContent>
      </Card>

      <JobseekerCertificateModal type="add" open={isAddOpen} onClose={handleCloseAdd} />

      {editCertificate && (
        <JobseekerCertificateModal
          type="edit"
          open={!!editCertificate}
          onClose={handleCloseEdit}
          certificate={editCertificate}
        />
      )}
    </>
  );
}; 